import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiList, FiLogIn, FiLogOut, FiMail, FiMoon, FiSun } from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";
import { localizedPath } from "../../utils/routeHelpers";
import useAuth from "../../hooks/useAuth";
import AccessibilityMenu from "../../components/AccessibilityMenu";
import styles from "./PortalPages.module.css";

/// Rama portalu publicznego: pasek marki (zawsze ciemny — jak sidebar aplikacji),
/// obszar treści i stopka. Wspólna dla kreatora wniosku, strony statusu i zakładania
/// konta, żeby trzy publiczne widoki wyglądały jak jeden serwis, a nie trzy osobne strony.
///
/// Szerokość robocza to 1440 px (jak panele mPracownika) — wąska kolumna sprawiała, że
/// wniosek wyglądał jak prosty formularz kontaktowy, a nie jak system obsługi ruchu osobowego.
/// `narrow` zostaje dla widoków bez panelu bocznego (potwierdzenie wysyłki).
///
/// `bottomBar` deklaruje, że widok ma własny pasek akcji przyklejony do dołu OKNA (kreator
/// wniosku). Taki pasek jest `position: fixed`, więc wypada z układu i na telefonie zasłania
/// koniec dokumentu — rama musi wtedy zarezerwować miejsce pod stopką. Rama odpowiada za tę
/// rezerwę, bo tylko ona zna stopkę; sam widok potrafi odsunąć wyłącznie własną kolumnę.
const PortalShell = ({ children, narrow = false, bottomBar = false }) => {
    const { theme, toggleTheme } = useTheme();
    // Zalogowana osoba zewnętrzna dostaje w pasku wejście do SWOJEGO konta zamiast
    // zaproszenia do logowania — inaczej profil („Moje dane") był praktycznie nie do znalezienia.
    const { user, logout } = useAuth();
    // Pełne noty prawne w stopce zajmowały na telefonie ~230 px pod formularzem.
    // Zostają dostępne w jednym tapnięciu; od 768 px rozwijają się same.
    const [legalOpen, setLegalOpen] = useState(false);

    return (
        <div className={`${styles.page} ${bottomBar ? styles.pageBottomBar : ""}`}>
            <header className={styles.topbar}>
                <div className={styles.topbarInner}>
                    {/* Znak firmowy = LOGO Krosno Glass (ten sam plik co w aplikacji i na PDF-ach),
                        a nie ikona — portal ma być rozpoznawalny jako serwis spółki. */}
                    <Link to="/wniosek" className={styles.brand}>
                        <img src="/logo.png" alt="Krosno Glass" className={styles.brandLogo} />
                        <span className={styles.brandText}>
                            <span className={styles.brandName}>Biuro Przepustek</span>
                            <span className={styles.brandCompany}>Krosno Glass S.A.</span>
                        </span>
                    </Link>

                    <div className={styles.topbarActions}>
                        {/* Na wąskich ekranach zostaje sama ikona — stąd aria-label.
                            Zalogowany ma w portalu JEDNO wejście: powrót do aplikacji (tam jest
                            jego pulpit, wnioski i dane — decyzja klienta 2026-08-08). Portal to
                            serwis dla osób BEZ konta. */}
                        {user ? (
                            <>
                                <Link
                                    to={localizedPath("dashboard")}
                                    className={styles.topbarLink}
                                    aria-label="Przejdź do aplikacji"
                                >
                                    <FiList size={14} />
                                    <span className={styles.topbarLinkLabel}>Przejdź do aplikacji</span>
                                </Link>
                                {/* Portal otwiera się na cudzym sprzęcie (telefon gościa, recepcja),
                                    a sesja stoi na ciasteczku — wylogowanie musi być pod ręką. */}
                                <button
                                    type="button"
                                    className={styles.topbarLink}
                                    onClick={() => logout("/wniosek")}
                                    aria-label="Wyloguj się"
                                >
                                    <FiLogOut size={14} />
                                    <span className={styles.topbarLinkLabel}>Wyloguj się</span>
                                </button>
                            </>
                        ) : (
                            <Link to="/logowanie" className={styles.topbarLink} aria-label="Zaloguj się">
                                <FiLogIn size={14} />
                                <span className={styles.topbarLinkLabel}>Zaloguj się</span>
                            </Link>
                        )}
                        {/* Dostępność (WCAG) także dla gościa bez konta — portal to często
                            pierwszy i jedyny kontakt osoby z systemem. */}
                        <AccessibilityMenu />
                        <button
                            type="button"
                            className={styles.themeBtn}
                            onClick={toggleTheme}
                            aria-label="Przełącz motyw"
                        >
                            {theme === "light" ? <FiMoon size={15} /> : <FiSun size={15} />}
                        </button>
                    </div>
                </div>
            </header>

            <div className={`${styles.main} ${narrow ? styles.mainNarrow : ""}`}>{children}</div>

            <footer className={`${styles.footer} ${legalOpen ? styles.footerLegalOpen : ""}`}>
                <div className={styles.footerInner}>
                    <div className={styles.footerCol}>
                        <div className={styles.footerBrand}>
                            <img src="/logo.png" alt="Krosno Glass" className={styles.footerLogo} />
                            <span className={styles.footerBrandText}>
                                <span className={styles.footerBrandName}>Biuro Przepustek</span>
                                <span className={styles.footerBrandCompany}>Krosno Glass S.A.</span>
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.footerLegalToggle}
                            onClick={() => setLegalOpen((value) => !value)}
                            aria-expanded={legalOpen}
                        >
                            Informacje prawne i administrator danych
                            <FiChevronDown size={15} className={styles.discloseChevron} />
                        </button>
                        <p className={`${styles.footerText} ${styles.footerLegalText}`}>
                            Administrator danych: KROSNO GLASS S.A., ul. Tysiąclecia 13, 38-400 Krosno.
                            Dane podane we wniosku przetwarzamy wyłącznie w celu obsługi ruchu osobowego
                            na terenie zakładu. Pełną klauzulę informacyjną oraz regulamin znajdziesz
                            w ostatnim kroku wniosku.
                        </p>
                    </div>

                    <div className={styles.footerCol}>
                        <h2 className={styles.footerHeading}>Kontakt</h2>
                        <div className={styles.footerLinks}>
                            <a className={styles.footerLink} href="mailto:biuroprzepustek@krosno.com">
                                <FiMail size={12} /> biuroprzepustek@krosno.com
                            </a>
                            <a className={styles.footerLink} href="mailto:iodo@krosno.com">
                                Inspektor ochrony danych: iodo@krosno.com
                            </a>
                        </div>
                    </div>

                    <div className={styles.footerCol}>
                        <h2 className={styles.footerHeading}>Portal</h2>
                        <div className={styles.footerLinks}>
                            <Link className={styles.footerLink} to="/wniosek">
                                Nowy wniosek o przepustkę
                            </Link>
                            <Link className={styles.footerLink} to="/logowanie">
                                Logowanie do konta
                            </Link>
                        </div>
                    </div>

                    <p
                        className={`${styles.footerText} ${styles.footerBar} ${styles.footerLegalText}`}
                    >
                        Portal służy do składania wniosków o przepustkę na teren zakładu. Wstęp odbywa się
                        na zasadach wewnętrznej instrukcji o ruchu osobowym — decyzję o wydaniu przepustki
                        podejmuje pracownik Biura Przepustek.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default PortalShell;
